/**
 * src/components/ui/Toast.tsx
 * -----------------------------
 * Global toast notification system (success, error, info, warning).
 *
 * Fully implemented this will:
 *   - Render a fixed portal at the top-right of the viewport.
 *   - Display up to 5 toasts simultaneously (FIFO queue).
 *   - Auto-dismiss after configurable duration (default 4s).
 *   - Support manual dismiss (×) button.
 *   - Animate entries (slide-in from right) and exits (fade + slide).
 *   - Color and icon per type: success (green), error (red), info (blue), warning (amber).
 *   - Export a `toast` helper function callable from anywhere without prop drilling.
 *     e.g. toast.success('Session saved')
 *         toast.error('Failed to send message')
 *
 * Key dependencies: framer-motion, zustand (toast store)
 */
import { useEffect } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { create } from 'zustand'

export type ToastType = 'success' | 'error' | 'info' | 'warning'

interface ToastItem {
  id: string
  type: ToastType
  message: string
  duration?: number
}

interface ToastStore {
  toasts: ToastItem[]
  add: (item: Omit<ToastItem, 'id'>) => void
  remove: (id: string) => void
}

const useToastStore = create<ToastStore>((set) => ({
  toasts: [],
  add: (item) =>
    set((s) => ({
      toasts: [
        ...s.toasts.slice(-4),
        { ...item, id: crypto.randomUUID() },
      ],
    })),
  remove: (id) =>
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}))

/** Programmatic toast helper usable outside React components */
export const toast = {
  success: (message: string, duration?: number) =>
    useToastStore.getState().add({ type: 'success', message, duration }),
  error: (message: string, duration?: number) =>
    useToastStore.getState().add({ type: 'error', message, duration }),
  info: (message: string, duration?: number) =>
    useToastStore.getState().add({ type: 'info', message, duration }),
  warning: (message: string, duration?: number) =>
    useToastStore.getState().add({ type: 'warning', message, duration }),
}

const TYPE_STYLES: Record<ToastType, string> = {
  success: 'border-l-4 border-green-500 bg-white',
  error:   'border-l-4 border-red-500   bg-white',
  info:    'border-l-4 border-blue-500  bg-white',
  warning: 'border-l-4 border-amber-400 bg-white',
}

const TYPE_ICONS: Record<ToastType, string> = {
  success: '✓',
  error:   '✕',
  info:    'ℹ',
  warning: '⚠',
}

function ToastItem({ item }: { item: ToastItem }) {
  const remove = useToastStore((s) => s.remove)

  useEffect(() => {
    const timer = setTimeout(() => remove(item.id), item.duration ?? 4000)
    return () => clearTimeout(timer)
  }, [item.id, item.duration, remove])

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 60, scale: 0.95 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 60, scale: 0.95 }}
      transition={{ type: 'spring', stiffness: 380, damping: 28 }}
      className={`flex items-start gap-3 rounded-card shadow-lg px-4 py-3 min-w-[280px] max-w-sm ${TYPE_STYLES[item.type]}`}
    >
      <span className="text-sm mt-0.5">{TYPE_ICONS[item.type]}</span>
      <p className="flex-1 text-sm text-gray-800 leading-snug">{item.message}</p>
      <button
        onClick={() => remove(item.id)}
        className="text-gray-300 hover:text-gray-500 text-sm ml-2"
        aria-label="Dismiss notification"
      >
        ×
      </button>
    </motion.div>
  )
}

/** Mount this once in App.tsx to render toasts globally */
export function Toast() {
  const toasts = useToastStore((s) => s.toasts)

  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      className="fixed top-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none"
    >
      <AnimatePresence mode="popLayout">
        {toasts.map((item) => (
          <div key={item.id} className="pointer-events-auto">
            <ToastItem item={item} />
          </div>
        ))}
      </AnimatePresence>
    </div>
  )
}
